import { useState, useRef, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { QRCodeSVG } from 'qrcode.react';
import { QrCode, Upload, Download, Copy, Check, Camera, Link, MessageSquare, Wifi, Info, Palette, IndianRupee } from 'lucide-react';

type QRType = 'url' | 'text' | 'wifi' | 'vcard' | 'upi';
type ActiveMode = 'generate' | 'scan';

const ERROR_LEVELS = ['L', 'M', 'Q', 'H'] as const;
type ErrorLevel = typeof ERROR_LEVELS[number];

const COLOR_PALETTE = [
  { label: 'Classic', fg: '#000000', bg: '#ffffff' },
  { label: 'Ocean',   fg: '#1e3a8a', bg: '#eff6ff' },
  { label: 'Forest',  fg: '#14532d', bg: '#f0fdf4' },
  { label: 'Sunset',  fg: '#7c2d12', bg: '#fff7ed' },
  { label: 'Candy',   fg: '#701a75', bg: '#fdf4ff' },
  { label: 'Slate',   fg: '#1e293b', bg: '#f8fafc' },
];

const QRCodeTools = () => {
  const [activeMode,    setActiveMode]    = useState<ActiveMode>('generate');
  const [qrType,        setQrType]        = useState<QRType>('url');
  const [inputValue,    setInputValue]    = useState('https://google.com');
  const [copied,        setCopied]        = useState(false);
  const [qrSize,        setQrSize]        = useState(256);
  const [errorLevel,    setErrorLevel]    = useState<ErrorLevel>('H');

  // WiFi
  const [ssid,       setSsid]       = useState('');
  const [password,   setPassword]   = useState('');
  const [encryption, setEncryption] = useState('WPA');

  // vCard
  const [vcName,  setVcName]  = useState('');
  const [vcPhone, setVcPhone] = useState('');
  const [vcEmail, setVcEmail] = useState('');

  // UPI
  const [upiId,      setUpiId]      = useState('');
  const [upiName,    setUpiName]    = useState('');
  const [upiAmount,  setUpiAmount]  = useState('');
  const [upiNote,    setUpiNote]    = useState('');

  // Branding
  const [fgColor,    setFgColor]    = useState('#000000');
  const [bgColor,    setBgColor]    = useState('#ffffff');
  const [activePalette, setActivePalette] = useState(0);

  // Scan
  const [scannedResult, setScannedResult] = useState<string | null>(null);
  const [processing,    setProcessing]    = useState(false);
  const [scanError,     setScanError]     = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const getVCardValue = () =>
    `BEGIN:VCARD\nVERSION:3.0\nFN:${vcName}\nTEL:${vcPhone}\nEMAIL:${vcEmail}\nEND:VCARD`;
  const getWifiValue = () => `WIFI:T:${encryption};S:${ssid};P:${password};;`;
  const getUpiValue = () => {
    const params = new URLSearchParams();
    params.set('pa', upiId);
    if (upiName)   params.set('pn', upiName);
    if (upiAmount) params.set('am', upiAmount);
    if (upiNote)   params.set('tn', upiNote);
    params.set('cu', 'INR');
    return `upi://pay?${params.toString()}`;
  };

  const finalValue =
    qrType === 'wifi'  ? getWifiValue() :
    qrType === 'vcard' ? getVCardValue() :
    qrType === 'upi'   ? (upiId ? getUpiValue() : 'upi://pay?pa=example@upi&cu=INR') :
    inputValue || 'https://example.com';

  const applyPalette = (idx: number) => {
    setActivePalette(idx);
    setFgColor(COLOR_PALETTE[idx].fg);
    setBgColor(COLOR_PALETTE[idx].bg);
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQR = () => {
    const svg = document.querySelector('#qr-code-svg') as SVGElement;
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas  = document.createElement('canvas');
    const ctx     = canvas.getContext('2d');
    const img     = new Image();
    img.onload = () => {
      const size = 1024;
      canvas.width = size; canvas.height = size;
      ctx?.fillStyle && (ctx.fillStyle = bgColor);
      ctx?.fillRect(0, 0, size, size);
      ctx?.drawImage(img, 0, 0, size, size);

      // For UPI: add centered label text
      if (qrType === 'upi' && ctx) {
        ctx.fillStyle = fgColor;
        ctx.font = 'bold 36px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('UPI ID QR CODE', size / 2, size - 30);
      }

      const link = document.createElement('a');
      link.download = `qrcode-${qrType}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const handleScan = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setProcessing(true); setScannedResult(null); setScanError(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const resp = await fetch('/api/tools/qr/scan', { method: 'POST', body: fd });
      const data = await resp.json();
      if (data.success) setScannedResult(data.data);
      else setScanError(data.error || 'Could not decode QR code');
    } catch { setScanError('Failed to scan QR code'); }
    finally { setProcessing(false); }
  };

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="tool-hero">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#e4c184] to-[#e4c084] flex items-center justify-center shadow-lg shadow-[#e4c184]/30">
            <QrCode size={16} className="text-white" />
          </div>
          <span className="text-xs font-semibold text-[#216974] bg-[#e4c184]/20 px-2.5 py-1 rounded-full">QR Studio</span>
        </div>
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">QR Code Studio</h1>
        <p className="text-sm text-gray-500 mt-1">
          Generate branded, high-resolution QR codes or decode any QR image instantly.
        </p>
      </div>

      {/* Mode toggle */}
      <div className="tab-pill-group w-fit mx-auto">
        <button onClick={() => setActiveMode('generate')} className={`tab-pill px-8 ${activeMode === 'generate' ? 'active' : ''}`}>
          <QrCode size={16} /> Generator
        </button>
        <button onClick={() => setActiveMode('scan')} className={`tab-pill px-8 ${activeMode === 'scan' ? 'active' : ''}`}>
          <Camera size={16} /> Scanner
        </button>
      </div>

      <AnimatePresence mode="wait">
        {activeMode === 'generate' ? (
          <motion.div key="gen" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="grid lg:grid-cols-[1fr_auto] gap-6">

            {/* Config column */}
            <div className="space-y-5">
              {/* Type */}
              <Card title="Content Type">
                <div className="tab-pill-group mb-5 flex-wrap">
                  {([
                    { id: 'url',   label: 'URL',    icon: <Link size={14} /> },
                    { id: 'text',  label: 'Text',   icon: <MessageSquare size={14} /> },
                    { id: 'wifi',  label: 'WiFi',   icon: <Wifi size={14} /> },
                    { id: 'vcard', label: 'vCard',  icon: <QrCode size={14} /> },
                    { id: 'upi',   label: 'UPI Pay', icon: <IndianRupee size={14} /> },
                  ] as { id: QRType; label: string; icon: ReactNode }[]).map((t) => (
                    <button key={t.id} onClick={() => setQrType(t.id)}
                      className={`tab-pill flex-1 ${qrType === t.id ? 'active' : ''}`}>
                      {t.icon} {t.label}
                    </button>
                  ))}
                </div>

                {(qrType === 'url' || qrType === 'text') && (
                  <Input
                    label={qrType === 'url' ? 'Target URL' : 'Text Content'}
                    value={inputValue} onChange={(e) => setInputValue(e.target.value)}
                    placeholder={qrType === 'url' ? 'https://example.com' : 'Your message…'}
                    leftIcon={qrType === 'url' ? <Link size={15} /> : <MessageSquare size={15} />}
                  />
                )}

                {qrType === 'wifi' && (
                  <div className="space-y-3">
                    <Input label="Network Name (SSID)" value={ssid} onChange={(e) => setSsid(e.target.value)} placeholder="WiFi Name" />
                    <Input label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="WiFi Password" />
                    <div>
                      <p className="section-label">Security</p>
                      <div className="flex gap-2">
                        {['WPA', 'WEP', 'None'].map((e) => (
                          <button key={e} onClick={() => setEncryption(e)}
                            className={`format-chip flex-1 text-center ${encryption === e ? 'active' : ''}`}>{e}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {qrType === 'vcard' && (
                  <div className="space-y-3">
                    <Input label="Full Name" value={vcName}  onChange={(e) => setVcName(e.target.value)}  placeholder="John Doe" />
                    <Input label="Phone"     value={vcPhone} onChange={(e) => setVcPhone(e.target.value)} placeholder="+1 555 000 0000" />
                    <Input label="Email"     value={vcEmail} onChange={(e) => setVcEmail(e.target.value)} placeholder="john@example.com" />
                  </div>
                )}

                {qrType === 'upi' && (
                  <div className="space-y-3">
                    {/* UPI info banner */}
                    <div className="flex items-start gap-3 p-3 rounded-xl bg-orange-50 border border-orange-200">
                      <IndianRupee size={16} className="text-orange-500 mt-0.5 shrink-0" />
                      <p className="text-xs text-orange-700 leading-relaxed">
                        When scanned, this QR will open <strong>PhonePe, Google Pay, Paytm, BHIM</strong> or any UPI app to complete payment.
                      </p>
                    </div>
                    <Input
                      label="UPI ID *"
                      value={upiId}
                      onChange={(e) => setUpiId(e.target.value)}
                      placeholder="yourname@paytm  /  9876543210@ybl"
                      leftIcon={<IndianRupee size={15} />}
                    />
                    <Input
                      label="Payee Name"
                      value={upiName}
                      onChange={(e) => setUpiName(e.target.value)}
                      placeholder="e.g. Raj Sharma"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        label="Amount (₹) — optional"
                        type="number"
                        value={upiAmount}
                        onChange={(e) => setUpiAmount(e.target.value)}
                        placeholder="0.00"
                      />
                      <Input
                        label="Note / Remark"
                        value={upiNote}
                        onChange={(e) => setUpiNote(e.target.value)}
                        placeholder="Payment for…"
                      />
                    </div>
                    {upiId && (
                      <div className="p-3 rounded-xl bg-gray-50 border border-gray-200">
                        <p className="text-[11px] text-gray-400 mb-1 font-medium uppercase tracking-wider">UPI Deep Link Preview</p>
                        <p className="font-mono text-xs text-gray-600 break-all">{getUpiValue()}</p>
                      </div>
                    )}
                  </div>
                )}
              </Card>

              {/* Design */}
              <Card title="Design & Branding" description="Color scheme and size settings">
                <div className="space-y-5">
                  {/* Palette presets */}
                  <div>
                    <p className="section-label flex items-center gap-1.5"><Palette size={11} /> Quick Palettes</p>
                    <div className="flex gap-2 flex-wrap">
                      {COLOR_PALETTE.map((p, i) => (
                        <button key={i} onClick={() => applyPalette(i)}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-medium transition-all duration-200 ${activePalette === i ? 'border-[#54c4b6] bg-[#216974]/10 text-[#216974] shadow-sm' : 'border-gray-200 text-gray-500 hover:border-gray-300 bg-white/70'}`}
                        >
                          <span className="w-3 h-3 rounded-full border border-gray-200 shrink-0" style={{ background: p.fg }} />
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Custom colors */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="section-label">Foreground</p>
                      <div className="flex items-center gap-3 p-2.5 rounded-xl border border-gray-200 bg-white/70">
                        <input type="color" value={fgColor} onChange={(e) => { setFgColor(e.target.value); setActivePalette(-1); }}
                          className="w-8 h-8 rounded-lg cursor-pointer border-0" />
                        <span className="font-mono text-xs text-gray-600 uppercase">{fgColor}</span>
                      </div>
                    </div>
                    <div>
                      <p className="section-label">Background</p>
                      <div className="flex items-center gap-3 p-2.5 rounded-xl border border-gray-200 bg-white/70">
                        <input type="color" value={bgColor} onChange={(e) => { setBgColor(e.target.value); setActivePalette(-1); }}
                          className="w-8 h-8 rounded-lg cursor-pointer border-0" />
                        <span className="font-mono text-xs text-gray-600 uppercase">{bgColor}</span>
                      </div>
                    </div>
                  </div>

                  {/* Size + Error level */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="section-label">Preview Size — {qrSize}px</p>
                      <input type="range" min="128" max="512" step="32" value={qrSize}
                        onChange={(e) => setQrSize(Number(e.target.value))} className="w-full" />
                    </div>
                    <div>
                      <p className="section-label">Error Correction</p>
                      <div className="flex gap-1">
                        {ERROR_LEVELS.map((l) => (
                          <button key={l} onClick={() => setErrorLevel(l)}
                            className={`format-chip flex-1 text-center ${errorLevel === l ? 'active' : ''}`}>{l}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* QR Preview */}
            <div className="flex flex-col items-center gap-4 lg:w-[300px]">
              <Card className="w-full" noPadding>
                <div className="p-6 flex flex-col items-center">
                  <motion.div
                    key={`${finalValue}-${fgColor}-${bgColor}-${qrSize}`}
                    initial={{ scale: 0.92, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    className="p-4 rounded-2xl shadow-inner"
                    style={{ background: bgColor }}
                  >
                    <QRCodeSVG
                      id="qr-code-svg"
                      value={finalValue}
                      size={Math.min(qrSize, 240)}
                      level={errorLevel}
                      fgColor={fgColor}
                      bgColor={bgColor}
                      includeMargin={false}
                    />
                  </motion.div>

                  {/* UPI label under QR */}
                  {qrType === 'upi' && (
                    <div className="mt-3 text-center">
                      <p className="text-[11px] font-bold tracking-widest text-gray-500 uppercase">UPI ID QR CODE</p>
                      {upiName && <p className="text-sm font-semibold text-gray-800 mt-0.5">{upiName}</p>}
                      {upiAmount && <p className="text-lg font-extrabold text-green-600 mt-0.5">₹{upiAmount}</p>}
                    </div>
                  )}

                  <div className="mt-5 space-y-2.5 w-full">
                    <Button onClick={downloadQR} size="sm" className="w-full" leftIcon={<Download size={15} />}>
                      Download 1024px PNG
                    </Button>
                    <Button variant="secondary" size="sm" className="w-full"
                      onClick={() => handleCopy(finalValue)}
                      leftIcon={copied ? <Check size={15} /> : <Copy size={15} />}
                    >
                      {copied ? 'Copied!' : 'Copy Data'}
                    </Button>
                  </div>
                </div>
              </Card>

              <div className="flex items-center gap-2 text-gray-400 text-[11px] bg-white/60 px-3 py-1.5 rounded-full border border-gray-200/60">
                <Info size={12} />
                Vector-sharp · High-res export
              </div>
            </div>
          </motion.div>
        ) : (
          /* ── Scanner ─────────────────────────────────────────────── */
          <motion.div key="scan" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="max-w-xl mx-auto space-y-5">
            <Card title="Upload QR Image">
              <div className="relative">
                {processing && (
                  <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10 rounded-2xl">
                    <div className="w-8 h-8 border-2 border-[#54c4b6] border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                <div
                  className="drop-zone p-12 text-center"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleScan} />
                  <div className="w-16 h-16 rounded-2xl bg-[#e4c184]/20 flex items-center justify-center mx-auto mb-4">
                    <Upload size={30} className="text-[#216974]" />
                  </div>
                  <p className="font-semibold text-gray-700">Upload QR Image to Decode</p>
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG, WebP · Any QR format</p>
                </div>
              </div>
            </Card>

            <AnimatePresence>
              {scanError && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="alert-error">
                  <Info size={16} className="shrink-0" />
                  <p className="text-sm font-medium">{scanError}</p>
                </motion.div>
              )}
              {scannedResult && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <Card title="Decoded Content" accent="green">
                    <div className="p-4 rounded-2xl bg-gray-50 border border-gray-200/60 font-mono text-sm text-gray-800 break-all select-all mb-4 leading-relaxed">
                      {scannedResult}
                    </div>
                    <div className="flex gap-3">
                      <Button variant="secondary" size="sm" className="flex-1"
                        onClick={() => handleCopy(scannedResult)}
                        leftIcon={copied ? <Check size={14} /> : <Copy size={14} />}
                      >
                        {copied ? 'Copied' : 'Copy Text'}
                      </Button>
                      {scannedResult.startsWith('http') && (
                        <Button size="sm" className="flex-1" leftIcon={<Link size={14} />}
                          onClick={() => window.open(scannedResult, '_blank')}>
                          Open URL
                        </Button>
                      )}
                    </div>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QRCodeTools;
